import { random } from './rng.js';

export type OklabColor = { L: number; a: number; b: number };
export type OklchColor = { L: number; C: number; h: number };

type CornerDiff = { hueStep: number; cMin: number; cMax: number };

// OKLCH → OKLab
export function oklchToOklab(L: number, C: number, h: number): OklabColor {
  const hRad = (h * Math.PI) / 180;
  return { L, a: C * Math.cos(hRad), b: C * Math.sin(hRad) };
}

// OKLab → linear sRGB (matrix from Björn Ottosson)
function oklabToLinearSRGB(L: number, a: number, b: number): { r: number; g: number; b: number } {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;
  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;
  return {
    r:  4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    b: -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s
  };
}

function linearToSRGB(c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

function clamp01(x: number): number { return Math.max(0, Math.min(1, x)); }

export function oklabToHex(L: number, a: number, b: number): number {
  const lin = oklabToLinearSRGB(L, a, b);
  const r   = Math.round(clamp01(linearToSRGB(lin.r)) * 255);
  const g   = Math.round(clamp01(linearToSRGB(lin.g)) * 255);
  const b8  = Math.round(clamp01(linearToSRGB(lin.b)) * 255);
  return (r << 16) | (g << 8) | b8;
}

export function lerpHue(h0: number, h1: number, t: number): number {
  const d = ((h1 - h0 + 540) % 360) - 180;
  return h0 + d * t;
}

// Difficulty: controls how distinct two line colors are
export const CORNER_DIFF: CornerDiff[] = [
  { hueStep: 80,  cMin: 0.26, cMax: 0.32 }, // Easy   — very distinct line colors
  { hueStep: 50,  cMin: 0.21, cMax: 0.26 }, // Normal
  { hueStep: 25,  cMin: 0.16, cMax: 0.20 }, // Hard   — subtle differences
];

// ── Crossword colour field ───────────────────────────────────────────────────
//
// A crossing cell has one colour but sits on two lines, so the two lines have
// to agree on it. That rules out giving each line its own private hue: whatever
// the crossing is coloured, it breaks one of them. The way out is to stop
// colouring line by line and colour the *board* instead — one continuous field
// that every line samples. Then a line is a straight cut through a smooth
// gradient, which is smooth by construction, and a crossing is simply the one
// point the two cuts share.
//
// Hue runs along the trunk and lightness runs across it, so:
//   hue       says which bar a tile is on   (bars sit at different columns)
//   lightness says how far along that bar it is
// Together they pin down exactly one cell, which is what makes it solvable.

export interface CrossField {
  baseHue: number;
  huePerCol: number;   // along the trunk — this is what tells bars apart
  huePerRow: number;   // slight tilt, so a bar isn't a flat single hue
  baseLight: number;
  lightPerRow: number; // across the trunk — position along a bar
  chroma: number;
}

// Per-difficulty steps between neighbouring cells.
const CROSS_STEPS = [
  { hue: 46, light: 0.075 }, // Easy
  { hue: 33, light: 0.060 }, // Normal
  { hue: 23, light: 0.048 }, // Hard
];

// How much smaller the drift across a line is than the step between lines.
const TILT_RATIO = 14;
// Widest lightness ramp to spread over, centred on 0.60.
const MAX_LIGHT_SPAN = 0.50;

export function randomCrossField(
  diffIndex: number, rows: number, cols: number, vivid: boolean,
): CrossField {
  const step = CROSS_STEPS[diffIndex];
  const d    = CORNER_DIFF[diffIndex];

  // On a wide board the hues would otherwise run right round the circle and
  // the last column would come back to meet the first. Leave at least one
  // column's worth of gap between the two ends.
  const hueStep = Math.min(step.hue, 360 / (cols + (rows - 1) / TILT_RATIO));
  // On a tall board the lightness ramp would run past what sRGB shows well.
  const lightStep = Math.min(step.light, MAX_LIGHT_SPAN / Math.max(1, rows - 1));

  const span   = lightStep * (rows - 1);
  const darker = random() < 0.5;
  const chroma = d.cMin + random() * (d.cMax - d.cMin);

  return {
    baseHue:   random() * 360,
    huePerCol: hueStep * (random() < 0.5 ? 1 : -1),
    // Kept far below huePerCol: across the longest possible line this drifts by
    // less than half a column step, so a line can never reach the hue of the
    // line in the next column along.
    huePerRow: (hueStep / TILT_RATIO) * (random() < 0.5 ? 1 : -1),
    baseLight:   0.60 + (darker ? -span / 2 : span / 2),
    lightPerRow: darker ? lightStep : -lightStep,
    // Vivid keeps full chroma; muted pulls it back, the same visible difference
    // the OKLab blend used to give.
    chroma: vivid ? chroma : chroma * 0.55,
  };
}

export function fieldOklch(f: CrossField, row: number, col: number): OklchColor {
  return {
    L: f.baseLight + f.lightPerRow * row,
    C: f.chroma,
    h: ((f.baseHue + f.huePerCol * col + f.huePerRow * row) % 360 + 360) % 360,
  };
}

export function fieldHex(f: CrossField, row: number, col: number): number {
  const { L, C, h } = fieldOklch(f, row, col);
  return oklchToHexFitted(L, C, h);
}

// ── Two-axis chromatic field ─────────────────────────────────────────────────
//
// Hue is a single angle, so a hue that varies along both axes is still one
// dimension: the iso-hue lines run diagonally across the board and only
// lightness tells the cells along one of them apart. Stepping along two
// perpendicular directions of the OKLab a-b plane instead gives two genuinely
// independent chromatic axes — across might run teal to pink while down runs
// lime to violet — and every cell lands on a point of its own.
//
// A lattice also carries its own floor: neighbouring cells differ by exactly
// the step, so the closest pair anywhere on the board *is* the step, rather
// than whatever two corners happened to land near each other.

/**
 * How much of the room a board has is actually used, per difficulty. This is a
 * share rather than a fixed distance because the ceiling moves: a 4x3 can
 * spread over most of the plane, an 8x6 has to fit 48 points into the same
 * space. Anchoring difficulty to the ceiling keeps the slider meaningful at
 * every size instead of every setting bottoming out on the big boards.
 */
const FIELD_SHARE = [1, 0.85, 0.7];

/**
 * However subtle Hard gets, neighbours stay this far apart. On the biggest
 * boards this is most of what's on offer anyway, so difficulty has little left
 * to give there — the same squeeze cross gets on a wide board.
 */
const FIELD_MIN_STEP = 0.02;
/** And Easy stops short of spanning the whole plane. */
const FIELD_MAX_STEP = 0.09;

/**
 * No tile may come nearer to grey than this. The middle of the a-b plane is
 * where the room is, so a patch left to its own devices settles right on top
 * of the neutral axis and the board turns to dust — this is what pushes it out
 * to where the colour is. Set to match what the bilinear grid holds.
 *
 * It costs: the ring it leaves is half the width of the whole plane, and the
 * patch has to fit inside that. Lowering it buys separation and loses colour.
 * Where sRGB is thin — the cyans reach barely 0.15 — it gives way rather than
 * squeezing the board into nothing.
 */
const FIELD_MIN_CHROMA = 0.1;

/** How far the patch may wander from the part of the wheel it was dealt. */
const FIELD_WANDER = 0.05;

/**
 * Lightness leans along the diagonal rather than down one of the axes, so
 * neither direction becomes "the light one". It's a tie-breaker — and the cue
 * that keeps a board readable without full colour vision — not a third
 * gradient, and any hue that can't take it gives way to the gamut below.
 */
const FIELD_TILT = 0.06;

/**
 * How much of a column's turn a row also takes.
 *
 * Kept vivid, a patch has to sit off the neutral axis, and there its two
 * natural directions are around the ring (hue) and out from it (chroma) — one
 * axis of hue and one of saturation, which is the same complaint as one of
 * hue and one of lightness. Leaning the rows sideways as they step outwards
 * puts a real turn of hue on that axis too. Neighbouring rows only get further
 * apart for it; the pair that sets the floor is still two along a row.
 */
const FIELD_SHEAR = 0.7;

/** The lightnesses a tile may be given, whatever its hue asks for. */
const FIELD_L_MIN = 0.45;
const FIELD_L_MAX = 0.85;

/** Compass directions, for walking the patch towards the roomiest spot. */
const AROUND: [number, number][] = [
  [1, 0], [0.7071, 0.7071], [0, 1], [-0.7071, 0.7071],
  [-1, 0], [-0.7071, -0.7071], [0, -1], [0.7071, -0.7071],
];

/** Is there any lightness at all that can show this hue at this chroma? */
function showableAnywhere(C: number, h: number): boolean {
  for (let L = FIELD_L_MIN; L <= FIELD_L_MAX + 1e-9; L += 0.02) {
    if (inGamut(L, C, h)) return true;
  }
  return false;
}

// How much chroma each hue can reach when lightness is free to suit it — 0.32
// in the magentas, barely 0.15 in the cyans. Pinning every tile to one
// lightness would cap the whole board at what its worst hue can manage there,
// and that cap is most of the room a board has to spread out in. Letting each
// tile take the lightness its own hue wants is what a vivid colour wheel does
// anyway: bright yellows, deep blues.
let chromaCeiling: Float64Array | null = null;
function ceilingFor(hue: number): number {
  if (!chromaCeiling) {
    const ceiling = new Float64Array(360);
    for (let h = 0; h < 360; h++) {
      let lo = 0, hi = 0.4;
      for (let i = 0; i < 18; i++) {
        const mid = (lo + hi) / 2;
        if (showableAnywhere(mid, h)) lo = mid; else hi = mid;
      }
      ceiling[h] = lo;
    }
    chromaCeiling = ceiling;
  }
  // A shade under, since the table is sampled a degree at a time.
  return chromaCeiling[((Math.round(hue) % 360) + 360) % 360] * 0.98;
}

/** The lightness nearest the one asked for that this hue and chroma can take. */
function lightnessFor(C: number, h: number, wanted: number): number {
  if (inGamut(wanted, C, h)) return wanted;
  let best = wanted, gap = Infinity;
  for (let L = FIELD_L_MIN; L <= FIELD_L_MAX + 1e-9; L += 0.01) {
    const d = Math.abs(L - wanted);
    if (d < gap && inGamut(L, C, h)) { gap = d; best = L; }
  }
  return best;
}

export function generateFieldColors(diffIndex: number, rows: number, cols: number): number[] {
  const baseL = 0.62 + (random() - 0.5) * 0.06;
  const tilt  = FIELD_TILT * (random() < 0.5 ? 1 : -1);
  const diag  = Math.max(1, cols - 1 + rows - 1);
  const wantedL = (c: number, r: number) => baseL + tilt * ((c + r) / diag - 0.5);

  const lean = FIELD_SHEAR * (random() < 0.5 ? 1 : -1);
  const at = (cx: number, cy: number, cs: number, sn: number, step: number, c: number, r: number) => {
    const v = (r - (rows - 1) / 2) * step;
    const u = (c - (cols - 1) / 2) * step + lean * v;
    return { a: cx + u * cs - v * sn, b: cy + u * sn + v * cs };
  };

  const fits = (cx: number, cy: number, cs: number, sn: number, step: number): boolean => {
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const { a, b } = at(cx, cy, cs, sn, step, c, r);
        const C = Math.hypot(a, b);
        const ceiling = ceilingFor((Math.atan2(b, a) * 180) / Math.PI);
        if (C > ceiling) return false;
        if (C < Math.min(FIELD_MIN_CHROMA, Math.max(0.04, ceiling - 0.06))) return false;
      }
    }
    return true;
  };

  /** The biggest the patch can get here before it runs out of colour. */
  const roomAt = (cx: number, cy: number, cs: number, sn: number): number => {
    if (fits(cx, cy, cs, sn, FIELD_MAX_STEP)) return FIELD_MAX_STEP;
    let lo = 0, hi = FIELD_MAX_STEP;
    for (let i = 0; i < 12; i++) {
      const mid = (lo + hi) / 2;
      if (fits(cx, cy, cs, sn, mid)) lo = mid; else hi = mid;
    }
    return lo;
  };

  // What's left to sit in is a ring: grey in the middle, the edge of sRGB
  // outside, and lumpy, since sRGB holds far more colour in some directions
  // than others. Which part of that ring a board gets is what makes one look
  // unlike the next, so it's dealt at random and then kept — left to hunt for
  // room, every board would end up in the magentas, where there's most of it.
  // Only the turn of the patch and a short wander from where it landed are
  // open to being chosen, and those are walked towards whatever room is
  // there: the difference between a board using the room it has and one using
  // two thirds of it.
  const ring = random() * Math.PI * 2;
  const home = { x: 0.15 * Math.cos(ring), y: 0.15 * Math.sin(ring) };
  const near = (x: number, y: number) => Math.hypot(x - home.x, y - home.y) <= FIELD_WANDER;

  let best = { room: 0, cx: home.x, cy: home.y, cs: 1, sn: 0 };
  for (let turn = 0; turn < 3; turn++) {
    const th = random() * Math.PI * 2;
    const cs = Math.cos(th), sn = Math.sin(th);
    let cx = home.x, cy = home.y;
    let room = roomAt(cx, cy, cs, sn);

    let delta = 0.03;
    for (let round = 0; round < 4 && room < FIELD_MAX_STEP; round++) {
      let moved = false;
      for (const [dx, dy] of AROUND) {
        const nx = cx + dx * delta, ny = cy + dy * delta;
        if (!near(nx, ny)) continue;
        const r = roomAt(nx, ny, cs, sn);
        if (r > room + 1e-4) { room = r; cx = nx; cy = ny; moved = true; }
      }
      if (!moved) delta *= 0.5;
    }

    if (room > best.room) best = { room, cx, cy, cs, sn };
    if (best.room >= FIELD_MAX_STEP) break;
  }

  const step = Math.max(best.room * FIELD_SHARE[diffIndex],
                        Math.min(best.room, FIELD_MIN_STEP));

  const colors: number[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const { a, b } = at(best.cx, best.cy, best.cs, best.sn, step, c, r);
      const C = Math.hypot(a, b);
      const h = (Math.atan2(b, a) * 180) / Math.PI;
      // Lightness only ever adds to how far apart two tiles are — the pair that
      // sets the floor is one the lattice has already spaced by `step`.
      colors.push(oklchToHexFitted(lightnessFor(C, h, wantedL(c, r)), C, h));
    }
  }
  return colors;
}

// ── Full-grid bilinear gradient (original mode) ──────────────────────────────

function randomCornerLch(diffIndex: number): OklchColor[] {
  const d       = CORNER_DIFF[diffIndex];
  const baseHue = random() * 360;
  const hues = [
    baseHue,
    baseHue + d.hueStep,
    baseHue + d.hueStep * 2,
    baseHue + d.hueStep * 3,
  ];
  return hues.map((h, i) => {
    const L = 0.57 + (i % 2 === 0 ? 0 : 0.07) + (random() - 0.5) * 0.06;
    const C = d.cMin + random() * (d.cMax - d.cMin);
    return { L, C, h: h % 360 };
  });
}

export function generateGridColors(diffIndex: number, rows: number, cols: number, vivid: boolean): number[] {
  const [TL, TR, BL, BR] = randomCornerLch(diffIndex);
  const colors: number[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const u = cols > 1 ? col / (cols - 1) : 0;
      const v = rows > 1 ? row / (rows - 1) : 0;
      if (vivid) {
        const topL = TL.L + (TR.L - TL.L) * u;
        const topC = TL.C + (TR.C - TL.C) * u;
        const topH = lerpHue(TL.h, TR.h, u);
        const botL = BL.L + (BR.L - BL.L) * u;
        const botC = BL.C + (BR.C - BL.C) * u;
        const botH = lerpHue(BL.h, BR.h, u);
        const L = topL + (botL - topL) * v;
        const C = topC + (botC - topC) * v;
        const h = lerpHue(topH, botH, v);
        colors.push(oklchToHexFitted(L, C, h));
      } else {
        const tlLab = oklchToOklab(TL.L, TL.C, TL.h);
        const trLab = oklchToOklab(TR.L, TR.C, TR.h);
        const blLab = oklchToOklab(BL.L, BL.C, BL.h);
        const brLab = oklchToOklab(BR.L, BR.C, BR.h);
        const w00 = (1 - u) * (1 - v), w10 = u * (1 - v), w01 = (1 - u) * v, w11 = u * v;
        const L = w00*tlLab.L + w10*trLab.L + w01*blLab.L + w11*brLab.L;
        const a = w00*tlLab.a + w10*trLab.a + w01*blLab.a + w11*brLab.a;
        const b = w00*tlLab.b + w10*trLab.b + w01*blLab.b + w11*brLab.b;
        colors.push(oklabToHexFitted(L, a, b));
      }
    }
  }
  return colors;
}

/** Can this OKLCH color be shown in sRGB without any channel being clipped? */
function inGamut(L: number, C: number, h: number): boolean {
  const { a, b } = oklchToOklab(L, C, h);
  const lin = oklabToLinearSRGB(L, a, b);
  const eps = 1e-4;
  return lin.r >= -eps && lin.r <= 1 + eps
      && lin.g >= -eps && lin.g <= 1 + eps
      && lin.b >= -eps && lin.b <= 1 + eps;
}

/**
 * The most chroma sRGB can actually show at this lightness and hue.
 *
 * The alternative — letting the renderer clip the RGB channels — silently
 * shifts both hue and lightness (by up to 36° and 0.06 at the vivid end), and
 * those two are exactly what the puzzle is read by. So chroma is what gives
 * way; hue and lightness come out exact.
 */
export function fitChroma(L: number, C: number, h: number): number {
  if (inGamut(L, C, h)) return C;
  let lo = 0, hi = C;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (inGamut(L, mid, h)) lo = mid; else hi = mid;
  }
  return lo;
}

/** Render an OKLCH color, easing chroma back until sRGB can hold it. */
export function oklchToHexFitted(L: number, C: number, h: number): number {
  const lab = oklchToOklab(L, fitChroma(L, C, h), h);
  return oklabToHex(lab.L, lab.a, lab.b);
}

/** Same, for a color that arrived as OKLab. */
export function oklabToHexFitted(L: number, a: number, b: number): number {
  const C = Math.hypot(a, b);
  if (C < 1e-6) return oklabToHex(L, a, b);
  return oklchToHexFitted(L, C, Math.atan2(b, a) * 180 / Math.PI);
}
